import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { MoreVertical } from 'lucide-react';
import { ReactNode, memo, useState } from 'react';

export interface DropdownMenuItem {
  /** Unique identifier for the action */
  id: string;
  /** Display label for the menu item */
  label: string;
  /** Icon to display next to the label */
  icon?: ReactNode;
  /** Click handler for the menu item */
  onClick: () => void;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Additional CSS classes for the menu item */
  className?: string;
  /** Whether this item represents a dangerous action (e.g., delete) */
  isDanger?: boolean;
}

export interface DropdownMenuProps {
  /** Array of menu items to display */
  items: DropdownMenuItem[];
  /** Custom trigger button content */
  trigger?: ReactNode;
  /** Additional CSS classes for the menu container */
  className?: string;
  /** Additional CSS classes for the trigger button */
  triggerClassName?: string;
  /** Callback when menu opens/closes */
  onOpenChange?: (isOpen: boolean) => void;
}

export const DropdownMenu = memo(
  ({ items, trigger, className = '', triggerClassName = '', onOpenChange }: DropdownMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleOpenChange = (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    };

    // Default trigger is the three dots button
    const defaultTrigger = (
      <button
        className={`flex h-8 w-8 items-center justify-center rounded-full p-0 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-seapoint-yellow focus-visible:ring-offset-2 ${
          isOpen
            ? 'border border-seapoint-yellow bg-white hover:bg-seapoint-yellow/10'
            : 'border border-gray-200 bg-white hover:bg-gray-50'
        } ${triggerClassName}`}
      >
        <MoreVertical className="h-4 w-4 text-gray-600" />
      </button>
    );

    return (
      <RadixDropdownMenu.Root onOpenChange={handleOpenChange}>
        <RadixDropdownMenu.Trigger asChild className={className}>
          {trigger || defaultTrigger}
        </RadixDropdownMenu.Trigger>

        <RadixDropdownMenu.Portal>
          <RadixDropdownMenu.Content
            align="end"
            sideOffset={4}
            className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[101] w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-xl"
          >
            {items.map((item, index) => (
              <RadixDropdownMenu.Item
                key={item.id}
                onSelect={item.onClick}
                disabled={item.disabled}
                className={`relative flex cursor-default select-none items-center gap-3 rounded-sm px-4 py-2 text-sm outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 ${
                  index === 0 ? 'rounded-t-md' : ''
                } ${index === items.length - 1 ? 'rounded-b-md' : ''} ${
                  item.isDanger ? 'text-red-600 focus:text-red-600' : ''
                } ${item.className || ''}`}
              >
                {item.icon && (
                  <span className={`${item.isDanger ? 'text-red-600' : 'text-gray-500'}`}>{item.icon}</span>
                )}
                {item.label}
              </RadixDropdownMenu.Item>
            ))}
          </RadixDropdownMenu.Content>
        </RadixDropdownMenu.Portal>
      </RadixDropdownMenu.Root>
    );
  },
  (prevProps, nextProps) => {
    // Compare the props that matter for rendering
    // We intentionally don't compare onClick functions since they're recreated on every render
    // and would cause unnecessary re-renders that close the dropdown
    return (
      prevProps.items.length === nextProps.items.length &&
      prevProps.items.every((item, index) => {
        const nextItem = nextProps.items[index];
        return (
          item.id === nextItem.id &&
          item.label === nextItem.label &&
          item.disabled === nextItem.disabled &&
          item.isDanger === nextItem.isDanger
        );
      }) &&
      prevProps.className === nextProps.className &&
      prevProps.triggerClassName === nextProps.triggerClassName
    );
  },
);

DropdownMenu.displayName = 'DropdownMenu';
